import moment   from 'moment';
import jsPDF    from 'jspdf';
import html2canvas from 'html2canvas';
import axios    from 'axios';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';

const reusableMixin = {

  // ─────────────────────────────────────────────────────────────────────────
  // Data
  // ─────────────────────────────────────────────────────────────────────────
  data() {
    return {
      stores:       [],
      getStore:     [{ value: 'All', name: 'All Stores' }],
      paymentTypes: [{ value: 'All', name: 'All Payment Types' }],
      toast:        useToast(),
      store:        useStore(),
      super_user:   null,
      company:      null,
      tableData: {
        store: null,
      },
    };
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Computed
  // ─────────────────────────────────────────────────────────────────────────
  computed: {
    permission() {
      return this.store.state.getCurrentUserPermission;
    },
    currentUser() {
      return this.store.state.getLoggedInUser;
    },
    currentUserCurrency() {
      return this.store.state.getLoggedInUser?.business?.business?.currency;
    },
    currentUserBusiness() {
      return this.store.state.getLoggedInUser?.business?.business;
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Methods
  // ─────────────────────────────────────────────────────────────────────────
  methods: {

    // ── Account & company ─────────────────────────────────────────────────

    superUser() {
      return axios.get('/user/account')
        .then(({ data }) => { this.super_user = data.data.data; })
        .catch(() => { /* intentionally silent */ });
    },

    async getCompany() {
      try {
        const { data } = await axios.get('/settings/company');
        this.company = data.data.data;
      } catch (_) { /* intentionally silent */ }
    },

    // ── Stores ────────────────────────────────────────────────────────────

    allStore() {
      axios.get('/admin/store/user-stores')
        .then(({ data }) => {
          this.stores   = data.data;
          this.getStore = [{ value: 'All', name: 'All Stores' }];
          this.stores.forEach(s => this.getStore.push({ value: s.store_name, name: s.store_name }));
          this.defaultStore();
        })
        .catch(() => { this.toast.error('Could not load stores. Please try again.'); });
    },

    defaultStore() {
      axios.get('/admin/store/default-store')
        .then(({ data }) => { this.tableData.store = data?.data?.store_name ?? null; })
        .catch(() => { /* intentionally silent */ });
    },

    // ── Payment types ──────────────────────────────────────────────────────

    allPaymentTypes() {
      axios.get('settings/payment_type')
        .then(({ data }) => {
          this.paymentTypes = [{ value: 'All', name: 'All Payment Types' }];
          data.data.forEach(p => this.paymentTypes.push({ value: p.payment_type, name: p.name }));
        })
        .catch(() => { /* intentionally silent */ });
    },

    // ── Date / time helpers ────────────────────────────────────────────────

    /**
     * Format a date value.
     * @param {string|Date} value
     * @param {'short'|'days ago'|''} type
     */
    dateTime(value, type = '') {
      if (!value) return '—';
      if (type === 'short')    return moment(value).format('Do MMM YYYY');
      if (type === 'days ago') return moment(value).fromNow();
      return moment(value).format('Do MMMM YYYY');
    },

    /**
     * Return today's date as MM/DD/YY for use in filenames.
     */
    formatDateMMDDYY() {
      return new Date()
        .toLocaleString()
        .split(/\D/)
        .slice(0, 3)
        .map(n => n.padStart(2, '0'))
        .join('/');
    },

    /**
     * Format a date with ordinal suffix: "4th June 2026".
     */
    formatDate(date) {
      if (!date) return '—';
      const d     = new Date(date);
      const day   = d.getDate();
      const month = d.toLocaleString('default', { month: 'long' });
      const year  = d.getFullYear();
      return `${day}${this._ordinal(day)} ${month} ${year}`;
    },

    _ordinal(n) {
      if (n > 3 && n < 21) return 'th';
      switch (n % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    },

    // Amounts are stored in Naira. Convert to GBP for non-NGN currencies.
    displayAmount (rawAmount, currency) {
      const NGN_TO_GBP_RATE = 2000;
      const n = Number(rawAmount) || 0;
      if (!currency) return n;
      const code = currency.code ?? '';
      return code !== 'NGN' && code !== '' ? n / NGN_TO_GBP_RATE : n;
    },

    formatPrice(value) {
      if (!value && value !== 0) return '0.00';
      return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    /**
     * Convert 24-hour "HH:MM" to 12-hour "H:MM AM/PM".
     */
    timeConvert(time) {
      if (!time) return time;
      const match = time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/);
      if (!match) return time;
      const parts  = match.slice(1);
      parts[4]     = +parts[0] < 12 ? 'AM' : 'PM';
      parts[0]     = +parts[0] % 12 || 12;
      return parts.join('');
    },

    // ── CSV export ─────────────────────────────────────────────────────────

    /**
     * Export the current table to a CSV file.
     *
     * Strategy (first match wins):
     *  1. Cells with class .row1 / .heading  (legacy explicit markup)
     *  2. Plain <td> / <th> elements inside .table or the first <table>
     *
     * Skips rows that are entirely empty after stripping commas so that
     * hidden / action / button-only columns don't produce blank columns.
     *
     * @param {string} filename - base filename without extension or date
     */
    reusableMixinToCSV(filename) {
      // Find the table — prefer element with class .table, fall back to first <table>
      const tableEl = document.querySelector('.table') ?? document.querySelector('table');
      if (!tableEl) {
        this.toast?.error('No table found to export.');
        return;
      }

      const rows = Array.from(tableEl.querySelectorAll('tr'));
      const csv  = [];

      rows.forEach(row => {
        // Try explicit classes first, then fall back to th/td
        let cells = Array.from(row.querySelectorAll('.row1, .heading'));
        if (!cells.length) {
          cells = Array.from(row.querySelectorAll('th, td'));
        }

        // Skip rows with no cells (e.g. colspan spacer rows)
        if (!cells.length) return;

        const values = cells.map(cell => {
          // Get visible text — skip icon/button content inside action columns
          // by preferring a data-value attribute when present
          let text = (cell.dataset.value ?? cell.innerText ?? '').trim();

          // Strip commas so they don't break CSV column boundaries
          text = text.replaceAll(',', '');

          // Skip cells that only contain whitespace, icons, or click handlers
          // (action column buttons, chevrons, etc.)
          if (!text || /^[\s\u00a0]*$/.test(text)) return '';

          // Preserve leading zeros (account codes, barcodes) with a quote prefix
          // so Excel doesn't truncate them
          if (/^0\d/.test(text)) return `'${text}`;

          // Wrap in double quotes if the value contains special characters
          if (text.includes('"') || text.includes('\n') || text.includes(';')) {
            return `"${text.replace(/"/g, '""')}"`;
          }

          return text;
        });

        // Drop rows where every value is empty (pure action/button rows)
        if (values.every(v => !v)) return;

        csv.push(values.join(','));
      });

      if (!csv.length) {
        this.toast?.warning('Nothing to export — the table appears to be empty.');
        return;
      }

      this._downloadCSV(csv.join('\n'), `${filename}-${this.formatDateMMDDYY()}.csv`);
    },

    _downloadCSV(csv, filename) {
      // BOM for Excel UTF-8 compatibility
      const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' });
      const a    = Object.assign(document.createElement('a'), {
        download: filename,
        href:     URL.createObjectURL(blob),
        style:    'display:none',
        target:   '_blank',
      });
      document.body.appendChild(a);
      a.click();
      // Cleanup after click
      setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 100);
    },

    // ── Print ──────────────────────────────────────────────────────────────

    reusableMixinToPrinter() {
      window.print();
    },

    /**
     * Open a print window containing the #printMe element.
     * @param {string} width   - CSS @page size value, e.g. 'A4', 'letter'
     * @param {string} fontsize - body font-size, e.g. '12px'
     * @param {string} title   - document title / filename prefix
     */
    printPage(width, fontsize = '12px', title = 'document') {
      const name    = `${title}-${this.formatDateMMDDYY()}`;
      const content = document.getElementById('printMe')?.innerHTML ?? '';
      const win     = window.open('', 'PRINT', 'height=700,width=900');
      if (!win) { this.toast.error('Pop-up blocked — please allow pop-ups and try again.'); return; }

      const isNarrow  = /^\d+mm$/.test(width) && parseInt(width) <= 80;
      const pageMargin = isNarrow ? '4mm' : '15mm';

      win.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${name}</title>
  <style>
    @page { size: ${width} auto; margin: ${pageMargin}; }
    *, *::before, *::after { box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; font-size: ${fontsize}; color: #212529; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 1rem; }
    th, td { padding: .4rem .5rem; border-bottom: 1px solid #dee2e6; text-align: left; }
    thead th { background: #f8f9fa; font-weight: 700; }
    /* Font Awesome inline SVG sizing */
    .svg-inline--fa { display:inline-block; height:1em; overflow:visible; vertical-align:-.125em; width:auto; }
    .me-1 { margin-right:.25rem; } .me-2 { margin-right:.5rem; }
    .text-center { text-align: center; }
    .text-right  { text-align: right; }
    .fw-bold     { font-weight: 700; }
    .d-flex      { display: flex; }
    .justify-content-between { justify-content: space-between; }
    .justify-content-end     { justify-content: flex-end; }
    .mt-1 { margin-top: .25rem; }  .mt-2 { margin-top: .5rem; }
    .mt-3 { margin-top: 1rem; }    .mt-4 { margin-top: 1.5rem; }
    .mb-1 { margin-bottom: .25rem; }.mb-2 { margin-bottom: .5rem; }
    .mb-3 { margin-bottom: 1rem; } .mb-4 { margin-bottom: 1.5rem; }
    .px-4 { padding-left: 1rem; padding-right: 1rem; }
    .py-3 { padding-top: 1rem; padding-bottom: 1rem; }
    .me-4 { margin-right: 1.5rem; }
    .bg-light { background: #f8f9fa; }
    .text-secondary { color: #6c757d; }
    .printdottedborder { border: 1px dotted #999; }
    .no-print { display: none !important; }
  </style>
</head>
<body>${content}</body>
</html>`);

      win.document.close();
      win.focus();
      // Small delay ensures styles are applied before print dialog opens
      setTimeout(() => { win.print(); win.close(); }, 250);
    },

    // ── PDF: table-based (jsPDF autoTable) ───────────────────────────────

    /**
     * Generate a PDF from structured data using jsPDF autoTable.
     * @param {object[]} dataToBeUsedArray   - row data objects
     * @param {string[]} headerArrayColumn   - column header labels
     * @param {'l'|'p'}  docOrientation
     * @param {string}   tableName           - used in filename
     * @param {string}   dataName            - used in header text
     * @param {string}   [width='a4']        - jsPDF format string
     * @param {any[]}    [footerRowArray=[]] - optional footer row
     */
    drawPdf(dataToBeUsedArray, headerArrayColumn, docOrientation, tableName, dataName, width = '', footerRowArray = []) {
      const rows = dataToBeUsedArray.map(item => Object.values(item));

      const pdf = new jsPDF({
        format:      width || 'a4',
        unit:        'mm',
        orientation: docOrientation,
      });

      pdf.setFont('Helvetica', 'bold');
      pdf.text('Mapiibs', 105, 10, { align: 'center' });
      pdf.setFont('Helvetica', 'normal');
      pdf.setFontSize(12);
      pdf.text(`${dataName} List`, 10, 20);
      pdf.setFontSize(8);
      pdf.text(`Generated: ${new Date().toISOString()}`, 200, 20, { align: 'right' });

      pdf.autoTable(headerArrayColumn, rows, {
        startY:     25,
        foot:       footerRowArray.length ? [footerRowArray] : undefined,
        headStyles: { fillColor: [5, 27, 53] },
        footStyles: { fillColor: [5, 27, 53] },
        styles:     { fontSize: 9, cellPadding: 3 },
        createdCell(cell) {
          if (cell?.styles?.fillColor) cell.styles.fillColor = '#f8f9fa';
        },
      });

      pdf.save(`${tableName}-${this.formatDateMMDDYY()}.pdf`);
    },

    // ── PDF: html2canvas multi-page ───────────────────────────────────────

    /**
     * Export a DOM element (this.$refs.pdfContent) to a multi-page PDF.
     * @param {string} title - filename (without .pdf)
     */
    async exportPDF(title) {
      const element = this.$refs.pdfContent;
      if (!element) { this.toast.error('PDF content element not found.'); return; }

      const canvas = await html2canvas(element, { scale: 2, useCORS: true, backgroundColor: '#fff' });
      const pdf    = new jsPDF('p', 'mm', 'a4');

      const margin     = 10;
      const pageWidth  = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth   = pageWidth - margin * 2;
      // Height in canvas pixels that corresponds to one PDF page
      const pxPerPage  = (canvas.width * (pageHeight - margin * 2)) / imgWidth;

      let y    = 0;
      let page = 0;

      while (y < canvas.height) {
        const sliceH   = Math.min(pxPerPage, canvas.height - y);
        const pageCanvas = Object.assign(document.createElement('canvas'), {
          width:  canvas.width,
          height: sliceH,
        });
        pageCanvas.getContext('2d').drawImage(canvas, 0, y, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

        const imgData   = pageCanvas.toDataURL('image/png');
        const imgHeight = (sliceH * imgWidth) / canvas.width;

        if (page > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, imgHeight);

        y    += pxPerPage;
        page += 1;
      }

      pdf.save(`${title}.pdf`);
    },

    // ── PDF: single-page html2canvas ──────────────────────────────────────

    /**
     * Render #printMe to a single-page PDF.
     * @param {string}  batch_code
     * @param {boolean} [is_adjustment=false] - landscape if true
     */
    async createPDF(batch_code, is_adjustment = false) {
      const element = document.getElementById('printMe');
      if (!element) { this.toast.error('Print element #printMe not found.'); return; }

      const pdf    = new jsPDF({ unit: 'pt', orientation: is_adjustment ? 'l' : 'p', lineHeight: 1.2 });
      const width  = pdf.internal.pageSize.getWidth();
      const height = pdf.internal.pageSize.getHeight();

      const canvas = await html2canvas(element, { scale: 2, useCORS: true, backgroundColor: '#fff' });
      const imgW = width;
      const imgH = (canvas.height / canvas.width) * width;
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, imgW, Math.min(imgH, height));
      pdf.save(`${batch_code}_${this.formatDateMMDDYY()}.pdf`);
    },

    // ── Misc ──────────────────────────────────────────────────────────────

    poweredBy() {
      return 'Powered by Mapiibs &mdash; <a href="https://www.mapiibs.co.uk" target="_blank">www.mapiibs.co.uk</a>';
    },
  },
};

export { reusableMixin };